import React, { useEffect } from 'react';

import { cn } from '@/libs';

import { uiConfig } from '@/config';
import { useAppDispatch } from '@/hooks';

import { GroupTitle, FormFieldMapper } from '..';
import { Checkbox } from '../inputs';
import { CheckboxGroupPresenter } from './CheckboxGroupPresenter';
import { GroupRestrictionErrors } from './GroupRestrictionErrors';
import { useClearOnNotApplicable, useNotApplicableField } from '../../hooks';
import { setConditionalFieldsGroup } from '../../store';
import type { ConditionalFieldsGroupFormField } from '../../typings';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';

import './ConditionalFieldsGroupPresenter.scss';

type ConditionalFieldsGroupPresenterProps = {
  conditionalFieldsGroup: ConditionalFieldsGroupFormField;
};

/**
 * Renders a conditional fields group presenter component.
 *
 * @internal
 * @component
 * @example
 * ```tsx
 * <ConditionalFieldsGroupPresenter conditionalFieldsGroup={conditionalFieldsGroup} />
 * ```
 *
 * @param props - The component props.
 * @param props.conditionalFieldsGroup - The conditional fields group object.
 * @returns The rendered component.
 */
export const ConditionalFieldsGroupPresenter: React.FC<
  React.PropsWithChildren<ConditionalFieldsGroupPresenterProps>
> = ({ conditionalFieldsGroup }) => {
  const dispatch = useAppDispatch();

  const containerClasses = cn(
    'cfg__container',
    uiConfig.grid.maxColumnsSpanClass,
  );

  const childrenClasses = cn(
    'grid',
    'gap-x-[20px]',
    'gap-y-[35px]',
    uiConfig.grid.columnsClass,
    'cfg__row',
  );

  const isFieldNA = useNotApplicableField(
    conditionalFieldsGroup.notApplicableField?.fieldId,
  );

  useClearOnNotApplicable(conditionalFieldsGroup, isFieldNA);

  useEffect(() => {
    dispatch(
      setConditionalFieldsGroup({
        groupId: conditionalFieldsGroup.fieldId,
        conditionFieldId: conditionalFieldsGroup.conditionField.fieldId,
      }),
    );
  }, [
    conditionalFieldsGroup.fieldId,
    conditionalFieldsGroup.conditionField.fieldId,
    dispatch,
  ]);

  return (
    // data-jump-scroll-only: this id names the whole group, not one field —
    // its first focusable descendant (the N/A checkbox, a condition option) is
    // not necessarily where a failed submit should land, so scroll only.
    <div
      id={conditionalFieldsGroup.fieldId}
      className={containerClasses}
      data-jump-scroll-only="true"
    >
      {conditionalFieldsGroup.name && (
        <GroupTitle title={conditionalFieldsGroup.name} />
      )}
      {conditionalFieldsGroup.alertMessage && (
        <FormFieldAlertMessage
          alertMessage={conditionalFieldsGroup.alertMessage}
        />
      )}

      {conditionalFieldsGroup.notApplicableField !== undefined && (
        <Checkbox
          name={conditionalFieldsGroup.notApplicableField.fieldId}
          label={conditionalFieldsGroup.notApplicableField.label}
          size="small"
        />
      )}

      {isFieldNA && conditionalFieldsGroup.naDetails && (
        <FormFieldMapper field={conditionalFieldsGroup.naDetails} />
      )}

      <CheckboxGroupPresenter field={conditionalFieldsGroup.conditionField} />

      <div className={childrenClasses}>
        {conditionalFieldsGroup.children.map(field => (
          <FormFieldMapper
            key={field.fieldId}
            field={field}
            disabled={isFieldNA}
          />
        ))}
      </div>

      {conditionalFieldsGroup.groupRestrictions?.map(groupRestriction => (
        // id gives the summary row a jump target — the restriction is keyed on
        // this branch group's own id, not the conditional group's (see
        // getOrderedValidationErrors.ts's emitGroupRestrictionRows). This div
        // renders empty whenever the branch has neither a current error nor a
        // standing hint — cfg__restriction:empty takes it out of the flex flow
        // so it stops reserving a gap in the happy path.
        <div
          key={groupRestriction.groupFieldId}
          id={groupRestriction.groupFieldId}
          className="cfg__restriction"
        >
          <GroupRestrictionErrors
            groupFieldId={groupRestriction.groupFieldId}
            restriction={groupRestriction.restriction}
            // The branch's children were flattened into the group's own list under
            // the branch id prefix — mirrors conditionalFieldsGroup/initialState.ts.
            fields={conditionalFieldsGroup.children.filter(child =>
              child.fieldId.startsWith(`${groupRestriction.groupFieldId}_`),
            )}
          />
        </div>
      ))}
    </div>
  );
};
